import { StateCreator } from 'zustand';
import { Asset } from '../../types/shared';
import type { AppState } from '../../store/app-state';
import { SetModel, BlockingPath, ChoreographySequence } from '../../types';
import { createLastModifiedBy } from '../../utils/lastModifiedBy';
import {
    batchSaveBlockingPaths,
    saveBlockingPath,
    saveChoreographySequence,
} from '@/features/sync/blocking-api';
import {
    markPendingBlockingPathWrite,
    markPendingChoreographyWrite,
} from '@/features/sync/blocking-pending-writes';
import {
    markPendingSetDelete,
    markPendingSetWrite,
} from '@/features/sync/set-pending-writes';
import { deleteSet } from '@/features/run-sheet/sets-api';
import type { ProjectScope } from '@/features/sync/project-scope';
import { showToast } from '@/features/layout/notifications';
import { logger } from '@/utils/logger';

export interface ProductionSlice {
    sets: SetModel[];
    blocking: BlockingPath[];
    choreography: ChoreographySequence[];
    assets: Asset[];
    productionActions: {
        // Sets
        addSet: (setModel: Partial<SetModel>) => string;
        updateSet: (id: string, updates: Partial<SetModel>) => void;
        deleteSet: (id: string) => void;
        
        // Blocking
        addBlockingPath: (path: Partial<BlockingPath>) => string;
        updateBlockingPath: (id: string, updates: Partial<BlockingPath>) => void;
        deleteBlockingPath: (id: string) => void;
        reorderBlockingPaths: (sceneId: string, oldIndex: number, newIndex: number) => void;
        replaceSceneBlocking: (sceneId: string, paths: BlockingPath[]) => void;
        
        // Choreography
        updateChoreography: (sequence: ChoreographySequence) => void;

        // Assets
        setAssets: (assets: Asset[]) => void;
        
        // Project scoping
        clearProduction: () => void;
    };
}

const PRODUCTION_PRESERVATION_RETRY_DELAYS_MS = [1000, 5000];

function persistProductionChange(label: string, action: () => Promise<void> | void, attempt = 0): void {
    try {
        const result = action();
        void Promise.resolve(result).catch(error => {
            logger.error(`[production] Failed to ${label}:`, error);
            const retryDelayMs = PRODUCTION_PRESERVATION_RETRY_DELAYS_MS[attempt];
            if (retryDelayMs !== undefined) {
                window.setTimeout(() => {
                    persistProductionChange(label, action, attempt + 1);
                }, retryDelayMs);
                return;
            }
            showToast('Blocking/choreography changes are still local. Keep this tab open while preservation recovers.', 'error');
        });
    } catch (error) {
        logger.error(`[production] Failed to ${label}:`, error);
        const retryDelayMs = PRODUCTION_PRESERVATION_RETRY_DELAYS_MS[attempt];
        if (retryDelayMs !== undefined) {
            window.setTimeout(() => {
                persistProductionChange(label, action, attempt + 1);
            }, retryDelayMs);
            return;
        }
        showToast('Blocking/choreography changes are still local. Keep this tab open while preservation recovers.', 'error');
    }
}

function getProjectScope(state: AppState & ProductionSlice): ProjectScope | null {
    if (!state.cloud.enabled || !state.cloud.orgId || !state.cloud.projectId) {
        return null;
    }
    return { orgId: state.cloud.orgId, projectId: state.cloud.projectId };
}

export const createProductionSlice: StateCreator<
    AppState & ProductionSlice,
    [["zustand/immer", never]],
    [],
    ProductionSlice
> = (set, get) => ({
    sets: [],
    blocking: [],
    choreography: [],
    assets: [],
    productionActions: {
        addSet: (setModel) => {
            const id = setModel.id || crypto.randomUUID();
            const newSet: SetModel = {
                name: setModel.name || 'New Set',
                width: setModel.width || 10,
                depth: setModel.depth || 8,
                ppm: setModel.ppm || 100,
                objects: [],
                ...setModel,
                id
            };
            set(state => {
                if (!state.sets) state.sets = [];
                state.sets.push(newSet);
            });
            const scope = getProjectScope(get());
            if (scope) {
                markPendingSetWrite(scope, id);
            }
            return id;
        },
        updateSet: (id, updates) => {
            let didUpdate = false;
            set(state => {
                const setModel = state.sets.find(s => s.id === id);
                if (setModel) {
                    Object.assign(setModel, updates);
                    didUpdate = true;
                }
            });
            const scope = getProjectScope(get());
            if (scope && didUpdate) {
                markPendingSetWrite(scope, id);
            }
        },
        deleteSet: (id) => {
            let didDelete = false;
            set(state => {
                didDelete = state.sets.some(s => s.id === id);
                state.sets = state.sets.filter(s => s.id !== id);
                if (didDelete) {
                    for (const scene of state.showStructure.scenes) {
                        if (scene.linkedSetId === id) {
                            scene.linkedSetId = null;
                        }
                    }
                }
            });
            const scope = getProjectScope(get());
            if (scope && didDelete) {
                markPendingSetDelete(scope, id);
                persistProductionChange('delete set', () => deleteSet(scope, id));
            }
        },
        
        addBlockingPath: (path) => {
            const id = crypto.randomUUID();
            const currentState = get();
            const lastModifiedBy = createLastModifiedBy(
                currentState.auth.user,
                currentState.cloud.member
            );
            const scenePaths = (currentState.blocking ?? [])
                .filter(p => !p.deleted && p.sceneId === path.sceneId);
            const maxOrder = scenePaths.reduce((max, p) => (p.order || 0) > max ? p.order || 0 : max, -1);
            const newPath: BlockingPath = {
                id,
                sceneId: path.sceneId || '',
                setId: path.setId || null,
                // Default values?
                ...path,
                order: maxOrder + 1,
                lastModifiedBy
            };
            set(state => {
                if (!state.blocking) state.blocking = [];
                state.blocking.push(newPath);
            });
            const scope = getProjectScope(get());
            if (scope) {
                markPendingBlockingPathWrite(scope, newPath.id);
                persistProductionChange('save blocking path', () => saveBlockingPath(scope, newPath));
            }
            return id;
        },
        updateBlockingPath: (id, updates) => {
            set(state => {
                const path = state.blocking.find(p => p.id === id);
                if (path) {
                    const lastModifiedBy = createLastModifiedBy(
                        state.auth.user,
                        state.cloud.member
                    );
                    Object.assign(path, updates, { lastModifiedBy });
                }
            });
            const scope = getProjectScope(get());
            const updatedPath = get().blocking.find(p => p.id === id);
            if (scope && updatedPath) {
                markPendingBlockingPathWrite(scope, updatedPath.id);
                persistProductionChange('save blocking path', () => saveBlockingPath(scope, updatedPath));
            }
        },
        deleteBlockingPath: (id) => {
            set(state => {
                const path = state.blocking.find(p => p.id === id);
                if (path) {
                    path.deleted = true;
                    path.deletedAt = Date.now(); // Soft delete usually for blocking
                    path.lastModifiedBy = createLastModifiedBy(
                        state.auth.user,
                        state.cloud.member
                    );
                }
            });
            const scope = getProjectScope(get());
            const deletedPath = get().blocking.find(p => p.id === id);
            if (scope && deletedPath) {
                markPendingBlockingPathWrite(scope, deletedPath.id);
                persistProductionChange('save blocking path deletion', () => saveBlockingPath(scope, deletedPath));
            }
        },
        reorderBlockingPaths: (sceneId, oldIndex, newIndex) => {
            let shouldPreserveOrder = false;
            set(state => {
                const paths = state.blocking
                    .filter(p => !p.deleted && p.sceneId === sceneId)
                    .toSorted((a, b) => (a.order || 0) - (b.order || 0));

                if (oldIndex < 0 || newIndex < 0 || oldIndex >= paths.length || newIndex >= paths.length) return;

                const [moved] = paths.splice(oldIndex, 1);
                paths.splice(newIndex, 0, moved!);

                for (const [index, p] of paths.entries()) {
                    const target = state.blocking.find(tp => tp.id === p.id);
                    if (target) target.order = index;
                }
                shouldPreserveOrder = true;
            });
            const scope = getProjectScope(get());
            const reorderedPaths = get().blocking
                .filter(p => !p.deleted && p.sceneId === sceneId)
                .toSorted((a, b) => (a.order || 0) - (b.order || 0));
            if (scope && shouldPreserveOrder && reorderedPaths.length > 0) {
                for (const path of reorderedPaths) {
                    markPendingBlockingPathWrite(scope, path.id);
                }
                persistProductionChange('save blocking path order', () => batchSaveBlockingPaths(scope, reorderedPaths));
            }
        },
        replaceSceneBlocking: (sceneId, paths) => {
            const normalizedPaths = paths.map(p => ({
                ...p,
                sceneId
            }));
            set(state => {
                // Remove all existing paths for this scene
                state.blocking = state.blocking.filter(p => p.sceneId !== sceneId);
                // Add new paths
                // Ensure they have the correct sceneId
                for (const p of normalizedPaths) {
                    state.blocking.push(p);
                }
            });
            const scope = getProjectScope(get());
            if (scope && normalizedPaths.length > 0) {
                for (const path of normalizedPaths) {
                    markPendingBlockingPathWrite(scope, path.id);
                }
                persistProductionChange('replace scene blocking', () => batchSaveBlockingPaths(scope, normalizedPaths));
            }
        },
        updateChoreography: (sequence) => {
            set(state => {
                if (!state.choreography) state.choreography = [];
                const index = state.choreography.findIndex(c => c.id === sequence.id);
                if (index !== -1) {
                    state.choreography[index] = sequence;
                } else {
                    state.choreography.push(sequence);
                }
            });
            const scope = getProjectScope(get());
            if (scope) {
                markPendingChoreographyWrite(scope, sequence.id);
                persistProductionChange('save choreography sequence', () => saveChoreographySequence(scope, sequence));
            }
        },
        setAssets: (assets) => set(state => {
            state.assets = assets;
        }),
        
        clearProduction: () => set(state => {
            state.sets = [];
            state.blocking = [];
            state.choreography = [];
            state.assets = [];
        })
    }
});












